import type { Message } from 'app/types/chat';
import { useEffect, useState } from 'react';
import type { AppStateStatus } from 'react-native';

export const SUPPORT_REPLY_POLL_INTERVAL_MS = 2_000;
export const SUPPORT_REPLY_WAIT_TIMEOUT_MS = 30_000;

interface PendingSupportReply {
  channelUrl: string;
  requestMessageId: string;
  startedAt: number;
}

export function useSupportReplyWait({
  appState,
  channelUrl,
  enabled,
  isFocused,
  isSupportChannel,
  messages,
  refetch,
}: {
  appState: AppStateStatus;
  channelUrl: string;
  enabled: boolean;
  isFocused: boolean;
  isSupportChannel: boolean;
  messages: Message[];
  refetch: () => Promise<unknown>;
}) {
  const [pendingState, setPendingState] = useState<{
    channelUrl: string;
    replies: Record<string, PendingSupportReply>;
  }>({ channelUrl, replies: {} });
  const repliedToRequestMessageIds = new Set(
    messages.flatMap((message) => (message.user?.isSupport ? [message.reply_to?.message_id] : [])),
  );
  const pendingEntries = Object.entries(pendingState.replies);
  const unansweredEntries = pendingEntries.filter(
    ([requestMessageId]) => !repliedToRequestMessageIds.has(requestMessageId),
  );
  const sourceChanged = pendingState.channelUrl !== channelUrl;
  const shouldClear = !enabled || !isSupportChannel;
  let currentPendingState = pendingState;
  if (
    sourceChanged ||
    (shouldClear && pendingEntries.length > 0) ||
    unansweredEntries.length !== pendingEntries.length
  ) {
    currentPendingState = {
      channelUrl,
      replies: sourceChanged || shouldClear ? {} : Object.fromEntries(unansweredEntries),
    };
    setPendingState(currentPendingState);
  }

  const pendingSupportReplies = Object.values(currentPendingState.replies);
  const hasPendingSupportReplies = pendingSupportReplies.length > 0;
  const earliestPendingStartedAt = hasPendingSupportReplies
    ? Math.min(...pendingSupportReplies.map((pending) => pending.startedAt))
    : undefined;

  const markSupportReplyQueued = (requestMessageId: string) => {
    if (!enabled || !isSupportChannel) return;
    setPendingState((current) => {
      const replies = current.channelUrl === channelUrl ? current.replies : {};
      return {
        channelUrl,
        replies: {
          ...replies,
          [requestMessageId]: { channelUrl, requestMessageId, startedAt: Date.now() },
        },
      };
    });
  };

  useEffect(() => {
    if (earliestPendingStartedAt === undefined) return;
    const nextExpirationDelay = SUPPORT_REPLY_WAIT_TIMEOUT_MS - (Date.now() - earliestPendingStartedAt);
    const clearExpiredReplies = () =>
      setPendingState((current) => {
        if (current.channelUrl !== channelUrl) return current;
        const expiredRequestMessageIds = Object.entries(current.replies).flatMap(([requestMessageId, pending]) =>
          Date.now() - pending.startedAt >= SUPPORT_REPLY_WAIT_TIMEOUT_MS ? [requestMessageId] : [],
        );
        if (expiredRequestMessageIds.length === 0) return current;

        const expired = new Set(expiredRequestMessageIds);
        return {
          ...current,
          replies: Object.fromEntries(
            Object.entries(current.replies).filter(([requestMessageId]) => !expired.has(requestMessageId)),
          ),
        };
      });

    const timeout = setTimeout(clearExpiredReplies, Math.max(0, nextExpirationDelay));
    return () => clearTimeout(timeout);
  }, [channelUrl, earliestPendingStartedAt]);

  useEffect(() => {
    if (!hasPendingSupportReplies || !isFocused || appState !== 'active') return;
    const interval = setInterval(() => void refetch(), SUPPORT_REPLY_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [appState, hasPendingSupportReplies, isFocused, refetch]);

  return { isSupportReplyPending: hasPendingSupportReplies, markSupportReplyQueued };
}
